import React, {
    useCallback, useContext, useEffect, useState, useMemo
} from 'react';
import { renderContentFromMetadata } from '@jutro/uiconfig';
import { EndorsementService } from 'gw-capability-policychange';
import { Loader } from '@jutro/components';
import _ from 'lodash';
import { TranslatorContext } from '@jutro/locale';
import {
    SgDateUtil,
    SgCepChanges,
    SgActiveTestsContext,
    ADOBE_TOPICS,
    useSgTrackPageName,
    SgSessionStorageService
} from 'saga-modules';
import CommonConstants from 'sg-common-constants';
import SgCepSideMenu from 'sg-cep-side-menu';
import { publish } from '@jutro/events';
import { BreakpointTrackerContext } from '@jutro/layout';
import {
    SgVehicleChangesSummary,
    SgDriverChangesSummary,
    SgCoverageChangesSummary,
    SgExcessChangesTable,
    SgEndorsementPremiumDetails,
    SgEndorsementChangesInfo,
    CepConstants
} from 'saga-cep-specific-components';
import { ViewModelServiceContext } from 'gw-portals-viewmodel-react';
import { WizardPage } from 'gw-portals-wizard-react';
import metadata from './SgEndorsementQuotePage.metadata.json5';
import metadataWOC5379 from './SgEndorsementQuotePage_WOC5379.metadata.json5';
import messages from './SgEndorsementQuotePage.messages';

function SgEndorsementQuotePage(props) {
    const {
        wizardData: modelVM,
        updateWizardData: updateData,
        goNext,
        sgWizardPropsExt
    } = props;

    const {
        handleCancel,
        handleGoBack
    } = sgWizardPropsExt;

    const translator = useContext(TranslatorContext);
    const viewModelService = useContext(ViewModelServiceContext);
    const { activeMvtTests } = useContext(SgActiveTestsContext);
    const breakpoint = useContext(BreakpointTrackerContext);
    const isMobile = breakpoint === 'tablet' || breakpoint === 'phoneWide' || breakpoint === 'phone';

    useSgTrackPageName(CepConstants.PAGE_NAMES.SUMMARY);

    const getValue = useCallback((value) => {
        const typelistValue = viewModelService.productMetadata.get('pc').types.getTypelist(value.typelist.name).getCode(value.code).name;
        return translator({ id: typelistValue, defaultMessage: typelistValue }).toString();
    }, [translator, viewModelService.productMetadata]);

    const [isLoading, setIsLoading] = useState(true);
    const [hasError, setHasError] = useState(false);
    const [changes, setChanges] = useState(SgCepChanges.getMotorChanges(modelVM, getValue));

    useEffect(() => {
        if (_.get(modelVM, 'status.value.code') === 'Draft') {
            EndorsementService.quoteEndorsement(modelVM.jobID.value).then((response) => {
                const policyChangeValueCopy = _.cloneDeep(modelVM.value);
                _.merge(policyChangeValueCopy, response);
                modelVM.value = policyChangeValueCopy;
                updateData(modelVM);
                setChanges(SgCepChanges.getMotorChanges(modelVM, getValue));
                publish(ADOBE_TOPICS.MTA_CHANGES, {
                    changes
                });
                publish(ADOBE_TOPICS.UPDATE_JOURNEY_PRODUCT, {
                    modelVM
                });
                SgSessionStorageService.setItem(SgSessionStorageService.KEY.MODEL, modelVM.value);
                setIsLoading(false);
            }).catch((error) => {
                setHasError(true);
                setIsLoading(false);
                if (_.isNil(error.appErrorCode)) {
                    _.set(error, 'appErrorCode', CommonConstants.SG_ERROR_CODES.SAGA_WEBSITE_ERROR);
                }
                throw error;
            });
        } else {
            setIsLoading(false);
        }
    }, [modelVM, changes, getValue, updateData]);

    const comprehensiveCoverBannerContent = useMemo(() => {
        const contactUsPhoneNumber = CommonConstants.SAGA_PHONE_NUMBERS.SAGA_CEP_RENEWAL_COVER_LEVELS_PHONE_NUMBER;
        return (
            <p id="confirmRenewalWordingBanner">
                {translator(messages.ComprehensiveCoverLabel, {
                    phoneNumber1: isMobile
                        ? <a href={`tel:${contactUsPhoneNumber.replaceAll(' ', '')}`}>{contactUsPhoneNumber}</a>
                        : <b>{contactUsPhoneNumber}</b>
                })}
            </p>
        );
    }, [isMobile, translator]);

    const onContinue = useCallback(() => {
        setIsLoading(true);
        goNext();
    }, [goNext]);

    if (isLoading) {
        return <Loader loaded={!isLoading} />;
    }

    if (hasError) {
        return null;
    }

    const overrideProps = {
        effectiveDateSubHeader: {
            content: translator(messages.ChangeEffective, { date: SgDateUtil.toLongDate(modelVM.baseData.effectiveDate.value) })
        },
        cepEndorsementVehicleContainer: {
            visible: modelVM.lobData.privateMotor.selectedChanges.value.hasVehicleChanges,
        },
        cepEndorsementDriversContainer: {
            visible: modelVM.lobData.privateMotor.selectedChanges.value.hasDriverChanges,
        },
        cepEndorsementCoveragesContainer: {
            visible: modelVM.lobData.privateMotor.selectedChanges.value.hasCoveragesChanges,
        },
        cepEndorsementExcessesContainer: {
            visible: !_.isNil(changes.voluntaryExcess) || !_.isNil(changes.compulsoryExcess) || !_.isNil(changes.fireAndTheftExcess),
        },
        vehicleChangesSummaryComponent: {
            changes,
            modelVM,
            getValue
        },
        driverChangesSummaryComponent: {
            changes,
            getValue,
            showVehicleChanges: !modelVM.lobData.privateMotor.selectedChanges.value.hasVehicleChanges
        },
        coverageChangesSummaryComponent: {
            changes
        },
        excessChangesTableComponent: {
            changes,
            modelVM
        },
        cepEndorsementChangesInfoComponent: {
            changes,
            modelVM,
            getValue
        },
        premiumDetails: {
            modelVM
        },
        comprehensiveCoverBanner: {
            content: comprehensiveCoverBannerContent
        },
        continuePolicyChangeButton: {
            onClick: onContinue
        }
    };

    const resolvers = {
        resolveCallbackMap: {
            handleGoBack,
            handleCancel
        },
        resolveComponentMap: {
            SgVehicleChangesSummary,
            SgDriverChangesSummary,
            SgCoverageChangesSummary,
            SgExcessChangesTable,
            SgEndorsementChangesInfo,
            SgEndorsementPremiumDetails,
            SgCepSideMenu
        }
    };

    return (
        <WizardPage showPrevious={false} showNext={false}>
            {renderContentFromMetadata(activeMvtTests.includes(CommonConstants.ACTIVE_MVT_TESTS.WOC_5379)
                ? metadataWOC5379.pageContent : metadata.pageContent, overrideProps, resolvers)}
        </WizardPage>
    );
}

export default SgEndorsementQuotePage;
